/** Typed public-website analytics events — allowlisted names and properties only. */

export const PUBLIC_EVENTS = [
  'open_account_clicked',
  'client_login_clicked',
  'support_clicked',
  'contact_clicked',
  'strategy_viewed',
  'strategy_selected',
  'market_selected',
  'seo_landing_page_viewed',
  'risk_disclosure_opened',
  'cookie_consent_accepted',
  'cookie_consent_rejected',
  'landing_view',
  'create_account_focus',
  'create_account_submit',
  'create_account_success',
  'create_account_error',
  'explainer_play',
  'theme_changed',
  'risk_disclosure_clicked',
  'sign_in_clicked',
] as const

export type PublicEventName = (typeof PUBLIC_EVENTS)[number]

export const CTA_LOCATIONS = [
  'header',
  'hero',
  'strategy_section',
  'funding_section',
  'footer',
  'landing_page',
  'banner',
  'legal',
  'nav',
  'blog',
  'trade_landing',
] as const

export type CtaLocation = (typeof CTA_LOCATIONS)[number]

export type StrategyId = 'safe' | 'balance' | 'aggressive'
export type MarketId = 'gold' | 'silver'

export type PublicEventProps = {
  page_path?: string
  cta_location?: CtaLocation
  strategy?: StrategyId
  market?: MarketId
  ui_theme?: 'light' | 'dark'
  utm_source?: string
  utm_medium?: string
  utm_campaign?: string
  utm_content?: string
  utm_term?: string
}

const BLOCKED_PROP_NAMES = [
  'email',
  'phone',
  'name',
  'full_name',
  'password',
  'mt5',
  'login',
  'account_id',
  'wallet',
  'balance',
  'amount',
  'token',
  'access_token',
  'ip',
  'document',
  'note',
  'query',
  'search',
]

export function isPublicEventName(name: string): name is PublicEventName {
  return (PUBLIC_EVENTS as readonly string[]).includes(name)
}

export function sanitizeEventProps(input: Record<string, unknown> | PublicEventProps | undefined): PublicEventProps {
  if (!input) return {}
  const out: PublicEventProps = {}
  for (const [key, value] of Object.entries(input)) {
    const lower = key.toLowerCase()
    if (BLOCKED_PROP_NAMES.some((b) => lower.includes(b))) continue
    if (key === 'page_path' && typeof value === 'string') {
      out.page_path = normalizePath(value)
      continue
    }
    if (key === 'cta_location' && typeof value === 'string' && (CTA_LOCATIONS as readonly string[]).includes(value)) {
      out.cta_location = value as CtaLocation
      continue
    }
    if (key === 'strategy' && (value === 'safe' || value === 'balance' || value === 'aggressive')) {
      out.strategy = value
      continue
    }
    if (key === 'market' && (value === 'gold' || value === 'silver')) {
      out.market = value
      continue
    }
    if (key === 'ui_theme' && (value === 'light' || value === 'dark')) {
      out.ui_theme = value
      continue
    }
    if (
      (key === 'utm_source' ||
        key === 'utm_medium' ||
        key === 'utm_campaign' ||
        key === 'utm_content' ||
        key === 'utm_term') &&
      typeof value === 'string'
    ) {
      out[key] = value.slice(0, 64)
    }
  }
  return out
}

export function normalizePath(path: string): string {
  const bare = path.split('?')[0].split('#')[0] || '/'
  if (bare.length > 1 && bare.endsWith('/')) return bare.slice(0, -1)
  return bare.startsWith('/') ? bare : `/${bare}`
}

export function isTrackablePublicPath(path: string): boolean {
  const p = normalizePath(path)
  if (p === '/web-test' || p === '/login' || p === '/register') return false
  return true
}
